import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '../../base/base.component';
import { OrganizationStore } from '../../../services/stores/organization.store';
import { AlertService } from '../../../services/alert.service';
import { ORG_ENTITY_KEY, ORG_ENTITY_LABEL } from '../../../models/organization-export-entities';
import { OrgExportPackage } from '../../../models/organization-export.model';
import { buildOrgExportFileName } from '../../../services/helpers/org-export.helper';

interface EntityOption {
  key: string;
  label: string;
  count: number;
  checked: boolean;
  isArray: boolean;
}

type ExportMode = 'loading' | 'select' | 'error';

@Component({
  selector: 'app-org-export-dialog',
  templateUrl: './org-export-dialog.component.html',
  styleUrl: './org-export-dialog.component.scss',
  standalone: false,
})
export class OrgExportDialogComponent extends BaseComponent implements OnInit {
  mode: ExportMode = 'loading';
  errorMessage = '';
  private exportPackage: OrgExportPackage | null = null;

  entities: EntityOption[] = [];

  constructor(
    public dialogRef: MatDialogRef<OrgExportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { orgId: string; orgName: string },
    private orgStore: OrganizationStore,
    private alertService: AlertService,
  ) {
    super();
  }

  ngOnInit() {
    this.orgStore.$exportOrganization(this.data.orgId).subscribe({
      next: (pkg: OrgExportPackage) => {
        this.exportPackage = pkg;
        this.entities = [
          { key: ORG_ENTITY_KEY.Users, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Users], count: pkg.users?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.ApiKeys, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.ApiKeys], count: pkg.apiKeys?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.Groups, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Groups], count: pkg.groups?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.Roles, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Roles], count: pkg.roles?.length ?? 0, checked: true, isArray: true },
          {
            key: ORG_ENTITY_KEY.ResourcePermissions,
            label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.ResourcePermissions],
            count: pkg.resourcePermissions?.length ?? 0,
            checked: true,
            isArray: true,
          },
          { key: ORG_ENTITY_KEY.Directories, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Directories], count: pkg.directories?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.Projects, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Projects], count: pkg.projects?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.Blueprints, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Blueprints], count: pkg.blueprints?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.Workspaces, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Workspaces], count: pkg.workspaces?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.Nodes, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Nodes], count: pkg.nodes?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.Edges, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Edges], count: pkg.edges?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.Comments, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.Comments], count: pkg.comments?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.LocalizedNodes, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.LocalizedNodes], count: pkg.localizedNodes?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.TableSpaces, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.TableSpaces], count: pkg.tableSpaces?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.WikiPages, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.WikiPages], count: pkg.wikiPages?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.WikiPageVersions, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.WikiPageVersions], count: pkg.wikiPageVersions?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.WikiYjsUpdates, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.WikiYjsUpdates], count: pkg.wikiYjsUpdates?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.AuditHistory, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.AuditHistory], count: pkg.auditHistory?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.ExternalLinks, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.ExternalLinks], count: pkg.externalLinks?.length ?? 0, checked: true, isArray: true },
          { key: ORG_ENTITY_KEY.License, label: ORG_ENTITY_LABEL[ORG_ENTITY_KEY.License], count: pkg.license ? 1 : 0, checked: true, isArray: false },
        ].sort((a, b) => a.label.localeCompare(b.label));
        this.mode = 'select';
      },
      error: (err: any) => {
        this.errorMessage = err?.error?.detail ?? err?.error?.title ?? err?.error ?? err?.message ?? 'Failed to load export data.';
        if (typeof this.errorMessage !== 'string') {
          this.errorMessage = 'Failed to load export data. Please try again.';
        }
        this.mode = 'error';
      },
    });
  }

  get allChecked(): boolean {
    return this.entities.every((e) => e.checked);
  }

  get someChecked(): boolean {
    return this.entities.some((e) => e.checked) && !this.allChecked;
  }

  get hasSelection(): boolean {
    return this.entities.some((e) => e.checked);
  }

  toggleAll(checked: boolean) {
    this.entities.forEach((e) => (e.checked = checked));
  }

  confirmExport() {
    if (!this.exportPackage) {
      return;
    }

    const filtered: Partial<OrgExportPackage> = {
      exportVersion: this.exportPackage.exportVersion,
      exportedAt: this.exportPackage.exportedAt,
      exportedBy: this.exportPackage.exportedBy,
      organization: this.exportPackage.organization,
    };

    for (const entity of this.entities) {
      const key = entity.key as keyof OrgExportPackage;
      if (entity.checked) {
        (filtered as any)[key] = this.exportPackage[key];
      } else {
        (filtered as any)[key] = entity.isArray ? [] : null;
      }
    }

    const orgName = this.data?.orgName ?? 'organisation';
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = buildOrgExportFileName(this.data?.orgName);
    anchor.click();
    URL.revokeObjectURL(url);

    this.alertService.showSuccessMessage(`Organisation '${orgName}' exported successfully.`);
    this.dialogRef.close();
  }

  close() {
    this.dialogRef.close();
  }
}
